import classNames from 'classnames';
import * as React from 'react';
import { useEffect } from 'react';
import { useImageGallery } from 'ud-ui/components/image-gallery/modal';
import UDText from 'ud-ui/components/text';
import { useDropZoneInit } from 'ud-form/components/dropzoneV2/hooks/use-drop-zone-init';
import { Dropzone } from 'ud-form/components/dropzoneV2/subcomponents/dropzone';
import { Format } from 'ud-form/components/dropzoneV2/subcomponents/format';
import Image from 'ud-form/components/dropzoneV2/subcomponents/image';
import { FormDropZoneVariantProps } from 'ud-form/components/dropzoneV2/types';
import { Container } from '../styles';

interface IProps extends FormDropZoneVariantProps {
  imageSize?: number;
  isPreview?: boolean;
  label?: string;
}

export const DefaultDropzone: React.FC<IProps> = (props) => {
  const { className, imageSize = 100, multiple = false, label, ...otherProps } = props;
  const { images, ...rest } = useDropZoneInit<File | string>({ ...otherProps, multiple });
  const { handleImageClick, GalleryModalWrapper, setImages, images: galleryImages } = useImageGallery();

  // Cache File -> objectURL to avoid regenerating URLs on each render and causing effect loops
  const objectUrlMapRef = React.useRef<WeakMap<File, string>>(new WeakMap());
  const urlSetRef = React.useRef<Set<string>>(new Set());
  const getObjectUrl = (file: File) => {
    const map = objectUrlMapRef.current;
    let url = map.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      map.set(file, url);
      urlSetRef.current.add(url);
    }
    return url;
  };

  React.useEffect(() => {
    return () => {
      // Revoke all created object URLs on unmount to avoid memory leaks
      urlSetRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlSetRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const mapped = images.map((image) => {
      const url = image instanceof File ? getObjectUrl(image) : image;
      const name = image instanceof File ? image.name : image;
      return { url, name };
    });

    const isEqual =
      galleryImages.length === mapped.length &&
      galleryImages.every((img, i) => img.url === mapped[i].url && img.name === mapped[i].name);

    if (!isEqual) {
      setImages(mapped);
    }
  }, [images, galleryImages, setImages]);

  return (
    <Container className={className}>
      {label && (
        <UDText type="subhead" className="mb-2">
          {label}
        </UDText>
      )}
      <Dropzone {...rest} className={classNames('mb-2', { disabled: props.disabled })} />
      <div className={classNames('flex w-full', { 'mb-2': images.length > 0 })}>
        <Format />
      </div>
      {images.length > 0 && (
        <div className="flex w-full gap-2">
          {images.map((image, index) => {
            const preview = image instanceof File ? getObjectUrl(image) : image;
            const name = image instanceof File ? image.name : image;
            return <Image key={name} src={preview} alt={name} size={imageSize} onClick={() => handleImageClick(index)} />;
          })}
        </div>
      )}
      <GalleryModalWrapper />
    </Container>
  );
};
